/**
 * One visit's certificate, as get_contract_inspections returned it: what was found, how each open
 * line closed, the photos (tap to zoom), the reading and who inspected.
 *
 * A visit on another contract of the same car links to that contract's page; a visit between
 * contracts says so. A legacy row reads "Notes only — recorded before the checklist".
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
    GRADE_LABEL,
    KIND_LABEL,
    LEGACY_NOTE,
    SLOT_LABEL,
    SLOT_ORDER,
    formatInspectionDay,
    formatKm,
    formatKmChange,
    lineLabel,
    roundName,
    type Checklist,
    type Grade,
    type Visit,
    type VisitLine,
} from '@/lib/inspections';
import { GradeBadge, Pill, ResultBadge } from './InspectionChip';
import { PhotoStrip, PhotoThumb } from './PhotoZoom';

const GRADE_ORDER: Record<Grade, number> = { fix_now: 0, fix_soon: 1, fixed: 2, not_needed: 3, ok: 4 };
const OPEN: ReadonlySet<Grade | null> = new Set<Grade | null>(['fix_now', 'fix_soon']);

/** What a line says beyond its grade: how an open line closed, or that it was fixed on the spot. */
function lineHistory(line: VisitLine): string | null {
    if (line.grade === 'fixed') {
        return OPEN.has(line.previous_grade) ? `was ${GRADE_LABEL[line.previous_grade!]}` : 'found and fixed during the inspection';
    }
    if (line.grade === 'not_needed' && OPEN.has(line.previous_grade)) return `was ${GRADE_LABEL[line.previous_grade!]}`;
    if ((line.grade === 'fix_now' || line.grade === 'fix_soon') && OPEN.has(line.previous_grade)) {
        return line.previous_grade === line.grade ? 'carried' : `was ${GRADE_LABEL[line.previous_grade!]}`;
    }
    return null;
}

interface Props {
    visit: Visit;
    checklist: Checklist | null | undefined;
    /** The contract whose page this is; a visit on another one links to its own page. */
    contractId?: number | null;
    className?: string;
}

export default function InspectionCertificate({ visit, checklist, contractId, className }: Props) {
    const [showOk, setShowOk] = useState(false);
    const legacy = visit.kind === null;
    const lines = [...visit.lines].sort((a, b) => GRADE_ORDER[a.grade] - GRADE_ORDER[b.grade]);
    const flagged = lines.filter((l) => l.grade !== 'ok');
    const okLines = lines.filter((l) => l.grade === 'ok');
    const slotPhotos = SLOT_ORDER.flatMap((slot) =>
        visit.photos.filter((p) => p.slot === slot).map((p) => ({ id: p.id, url: p.url, caption: SLOT_LABEL[slot] })),
    );
    const loosePhotos = visit.photos.filter((p) => !p.slot && !p.line_id).map((p) => ({ id: p.id, url: p.url }));
    const change = formatKmChange(visit.odometer_change_km);

    return (
        <article id={`visit-${visit.id}`} className={cn('scroll-mt-32 rounded-xl border bg-card p-4 sm:p-5', className)}>
            <header className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="font-semibold">{formatInspectionDay(visit.inspected_on)}</p>
                    <p className="text-sm text-muted-foreground">
                        {legacy ? 'Notes only' : KIND_LABEL[visit.kind!]}
                        {visit.kind === 'monthly' && visit.period && ` · ${roundName(visit.period)}`}
                        {visit.inspector_first_name && ` · inspected by ${visit.inspector_first_name}`}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                    {visit.late && <Pill tone="orange">Late</Pill>}
                    {visit.released && <Pill tone="green">Released</Pill>}
                    {!legacy && <ResultBadge result={visit.result} />}
                </div>
            </header>

            {(visit.between_contracts || (contractId && visit.contract_id && visit.contract_id !== contractId)) && (
                <p className="mt-2 text-xs text-muted-foreground">
                    {visit.between_contracts ? (
                        'Between contracts — no driver sees this visit.'
                    ) : (
                        <>
                            On{' '}
                            <Link to={`/dashboard/inspections/contract/${visit.contract_id}`} className="underline underline-offset-2">
                                contract #{visit.contract_id}
                            </Link>
                        </>
                    )}
                </p>
            )}

            {legacy ? (
                <div className="mt-3 space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">{LEGACY_NOTE}</p>
                    {visit.remarks && <p className="whitespace-pre-wrap text-sm">{visit.remarks}</p>}
                    <PhotoStrip photos={loosePhotos} label="Inspection photo" />
                </div>
            ) : (
                <>
                    {(visit.odometer_km !== null || visit.last_service_km !== null) && (
                        <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                            {visit.odometer_km !== null && (
                                <div className="flex gap-1.5">
                                    <dt className="text-muted-foreground">Odometer</dt>
                                    <dd className="tabular-nums">
                                        {formatKm(visit.odometer_km)}
                                        {change && (
                                            <span className={cn('ml-1.5 text-xs', visit.odometer_change_km! < 0 ? 'text-orange-700 dark:text-orange-400' : 'text-muted-foreground')}>
                                                {change}
                                            </span>
                                        )}
                                    </dd>
                                </div>
                            )}
                            {visit.last_service_km !== null && (
                                <div className="flex gap-1.5">
                                    <dt className="text-muted-foreground">Last service at</dt>
                                    <dd className="tabular-nums">{formatKm(visit.last_service_km)}</dd>
                                </div>
                            )}
                        </dl>
                    )}

                    {flagged.length > 0 && (
                        <ul className="mt-3 divide-y rounded-lg border">
                            {flagged.map((line) => {
                                const photos = visit.photos.filter((p) => p.line_id === line.id).map((p) => ({ id: p.id, url: p.url }));
                                const history = lineHistory(line);
                                return (
                                    <li key={line.id} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-start sm:justify-between">
                                        <div className="min-w-0 space-y-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <GradeBadge grade={line.grade} />
                                                <span className="font-medium">{lineLabel(checklist, line.item)}</span>
                                                {history && <span className="text-xs text-muted-foreground">{history}</span>}
                                            </div>
                                            {line.note && <p className="whitespace-pre-wrap text-sm text-muted-foreground">{line.note}</p>}
                                        </div>
                                        <PhotoStrip photos={photos} label={lineLabel(checklist, line.item)} className="shrink-0" />
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {okLines.length > 0 && (
                        <div className="mt-3">
                            <button
                                type="button"
                                onClick={() => setShowOk((v) => !v)}
                                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                                aria-expanded={showOk}
                            >
                                <ChevronDown className={cn('h-4 w-4 transition-transform', showOk && 'rotate-180')} aria-hidden />
                                {okLines.length} {okLines.length === 1 ? 'line' : 'lines'} OK
                            </button>
                            {showOk && (
                                <ul className="mt-2 grid gap-1 text-sm text-muted-foreground sm:grid-cols-2">
                                    {okLines.map((l) => (
                                        <li key={l.id}>{lineLabel(checklist, l.item)}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {slotPhotos.length > 0 && (
                        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                            {slotPhotos.map((p) => (
                                <PhotoThumb key={p.id} url={p.url} alt={p.caption} caption={p.caption} className="h-16 w-16" />
                            ))}
                        </div>
                    )}

                    {visit.remarks && (
                        <p className="mt-3 whitespace-pre-wrap border-t pt-3 text-sm">
                            <span className="text-muted-foreground">Remarks: </span>
                            {visit.remarks}
                        </p>
                    )}
                </>
            )}
        </article>
    );
}
